import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ModalController } from "@ionic/angular";
import { ApiService } from "../../configuration/services/api.service";
import { RideOpsUiService } from "../../configuration/services/rideops-ui.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";
import { UiQuery } from "source/app/configuration/state/ui.query";
import { UiService } from "source/app/configuration/state/ui.service";
import { firstValueFrom, interval } from "rxjs";
import { StatisticsMetric } from "./statistics.types";

type StatisticsValueField = keyof LocationStatisticsResultItem;
type StatisticsDisplayType = "percent" | "number" | "time";

interface StatisticsWidgetConfig {
	metric: StatisticsMetric;
	labelKey: string;
	display: StatisticsDisplayType;
	field: StatisticsValueField;
}

@Component({
	selector: "app-statistics",
	templateUrl: "./statistics.component.html",
	styleUrls: ["./statistics.component.scss"],
})
export class StatisticsComponent implements OnInit {
	constructor(
		private apiService: ApiService,
		private modalController: ModalController,
		private uiQuery: UiQuery,
		private uiService: UiService,
		private rideOpsUi: RideOpsUiService,
		private locationStateQuery: LocationStateQuery,
	) {}

	private readonly destroyRef = inject(DestroyRef);
	private locationIsOpen = false;
	private uptimeOpenSince: number | null = null;
	private uptimeAccumulatedMs = 0;
	private uptimeBonusSeconds = 0;

	readonly StatisticsMetric = StatisticsMetric;
	readonly donutRadius = 72;
	readonly donutCircumference = 2 * Math.PI * this.donutRadius;

	readonly widgets: StatisticsWidgetConfig[] = [
		{
			metric: StatisticsMetric.DispatchUtilization,
			labelKey: "STATISTICS_PAGE__CART_UTILIZATION",
			display: "percent",
			field: "cartUtilization",
		},
		{
			metric: StatisticsMetric.Dispatches,
			labelKey: "STATISTICS_PAGE__DISPATCHES",
			display: "number",
			field: "dispatches",
		},
		{
			metric: StatisticsMetric.AverageDispatchTime,
			labelKey: "STATISTICS_PAGE__AVG_CYCLE_TIME",
			display: "time",
			field: "averageTime",
		},
		{
			metric: StatisticsMetric.CapacityUtilization,
			labelKey: "STATISTICS_PAGE__RIDE_UTILIZATION",
			display: "percent",
			field: "rideUtilization",
		},
		{
			metric: StatisticsMetric.Riders,
			labelKey: "STATISTICS_PAGE__RIDERS",
			display: "number",
			field: "riders",
		},
		{
			metric: StatisticsMetric.Uptime,
			labelKey: "STATISTICS_PAGE__UPTIME",
			display: "time",
			field: "uptime",
		},
	];

	statistics: LocationStatisticsResult;
	offline = false;

	dashboardStatisticsMetric$ = this.uiQuery.dashboardStatisticsMetric$;

	isV2 = (): boolean => this.rideOpsUi.isV2Active();

	select = async (metric: StatisticsMetric) => {
		const currentMetric = await firstValueFrom(this.dashboardStatisticsMetric$);
		const newMetric = currentMetric == metric ? StatisticsMetric.None : metric;
		this.uiService.setDashboardStatisticsMetric(newMetric);
	};

	getStatistics = async () => {
		this.offline = false;
		try {
			this.statistics = await this.apiService.getStatistics();
			this.resetUptimeClock();
		} catch {
			this.offline = true;
		}
	};

	getValue = (period: keyof LocationStatisticsResult, field: StatisticsValueField): number | null => {
		const value = this.statistics?.[period]?.[field] ?? null;
		if (value == null || field !== "uptime") {
			return value;
		}

		const live = value + this.uptimeBonusSeconds;
		// ponytail: last-hour window slides; cap at 3600s. Refetch if a downtime hole is still in the hour.
		return period === "lastHour" ? Math.min(live, 3600) : live;
	};

	donutDashOffset = (value: number | null): number => {
		if (value == null) {
			return this.donutCircumference;
		}

		const clamped = Math.max(0, Math.min(100, value));
		return this.donutCircumference * (1 - clamped / 100);
	};

	formatHeroTime = (totalSeconds: number | null): string | null => {
		if (totalSeconds == null) {
			return null;
		}

		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = Math.floor(totalSeconds % 60);
		const parts: string[] = [];

		if (hours > 0) {
			parts.push(`${hours}h`);
		}

		if (minutes > 0 || hours > 0) {
			parts.push(`${minutes}m`);
		}

		parts.push(`${seconds}s`);
		return parts.join(" ");
	};

	close = () => {
		this.modalController.dismiss();
	};

	ngOnInit() {
		this.locationStateQuery.locationIsOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isOpen) => {
			this.setLocationIsOpen(isOpen);
		});
		interval(1000)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.tickUptime());
		this.getStatistics();
	}

	private resetUptimeClock() {
		this.uptimeAccumulatedMs = 0;
		this.uptimeBonusSeconds = 0;
		this.uptimeOpenSince = this.locationIsOpen ? Date.now() : null;
	}

	private setLocationIsOpen(isOpen: boolean) {
		if (isOpen === this.locationIsOpen) {
			return;
		}

		if (this.locationIsOpen && this.uptimeOpenSince != null) {
			this.uptimeAccumulatedMs += Date.now() - this.uptimeOpenSince;
		}

		this.locationIsOpen = isOpen;
		this.uptimeOpenSince = isOpen ? Date.now() : null;
		this.tickUptime();
	}

	private tickUptime() {
		if (!this.statistics) {
			return;
		}

		const runningMs = this.uptimeOpenSince == null ? 0 : Date.now() - this.uptimeOpenSince;
		this.uptimeBonusSeconds = Math.floor((this.uptimeAccumulatedMs + runningMs) / 1000);
	}
}
